// apps/web/src/components/AuditDashboard.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AuditDashboard.css';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1',
});

// Automatically attach Authorization header
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default function AuditDashboard({ user }) {
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'events', 'security'
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [showFullDocs, setShowFullDocs] = useState(false);

  // Filters
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');

  useEffect(() => {
    if (activeTab === 'overview') {
      loadStats();
    } else if (activeTab === 'events') {
      loadEvents();
    } else if (activeTab === 'security') {
      loadSecurityEvents();
    }
  }, [activeTab, eventTypeFilter, severityFilter]);

  async function loadStats() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/audit/stats');
      setStats(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  }

  async function loadEvents() {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (eventTypeFilter) params.eventType = eventTypeFilter;
      if (severityFilter) params.severity = severityFilter;
      params.limit = 100;

      const res = await api.get('/audit/events', { params });
      setEvents(res.data.events || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }

  async function loadSecurityEvents() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/audit/security');
      setSecurityEvents(res.data.events || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load security events');
    } finally {
      setLoading(false);
    }
  }

  // SECURITY: Only 'owner' or 'admin' role can access audit logs (GDPR compliance)
  console.log('[AuditDashboard] Access check - user:', user, 'role:', user?.role);
  if (!user || (user.role !== 'owner' && user.role !== 'admin')) {
    console.log('[AuditDashboard] Access denied - only owner/admin role allowed, got:', user?.role);
    return (
      <div className="AuditDashboard">
        <div className="AuditDashboard-error">
          <h2>Access Denied</h2>
          <p>Audit logs are restricted to system administrators only (GDPR compliance).</p>
        </div>
      </div>
    );
  }
  console.log('[AuditDashboard] Access granted for owner role');

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#ef4444';
      case 'error': return '#f97316';
      case 'warning': return '#f59e0b';
      case 'info': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return '=4';
      case 'error': return '=�';
      case 'warning': return '=�';
      case 'info': return '=5';
      default: return '�';
    }
  };

  const getEventTypeIcon = (eventType) => {
    switch (eventType) {
      case 'auth': return '=';
      case 'security': return '=�';
      case 'gdpr': return '=�';
      case 'business': return '=�';
      case 'system': return '�';
      default: return '=�';
    }
  };

  return (
    <div className="AuditDashboard">
      <div className="AuditDashboard-header">
        <div className="AuditDashboard-title">
          <h1>= Audit Dashboard</h1>
          <p>Security & compliance monitoring</p>
        </div>

        <div className="AuditDashboard-tabs">
          <button
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => setActiveTab('overview')}
          >
            =� Overview
          </button>
          <button
            className={activeTab === 'events' ? 'active' : ''}
            onClick={() => setActiveTab('events')}
          >
            =� All Events
          </button>
          <button
            className={activeTab === 'security' ? 'active' : ''}
            onClick={() => setActiveTab('security')}
          >
            =� Security
          </button>
          <button
            className="AuditDashboard-help-btn"
            onClick={() => setShowGuide(true)}
          >
            ❓ Audit Help
          </button>
        </div>
      </div>

      {error && (
        <div className="AuditDashboard-error">
          <p>{error}</p>
        </div>
      )}

      {loading && (
        <div className="AuditDashboard-loading">
          <p>Loading...</p>
        </div>
      )}

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && stats && !loading && (
        <div className="AuditDashboard-content">
          {/* Statistics Grid */}
          <div className="AuditDashboard-grid">
            {/* Event Types Card */}
            <div className="AuditDashboard-card">
              <h3>Event Types (Last 30 Days)</h3>
              <div className="AuditDashboard-stats">
                {stats.eventTypes.map((et) => (
                  <div key={et.event_type} className="AuditDashboard-stat">
                    <span className="stat-icon">{getEventTypeIcon(et.event_type)}</span>
                    <span className="stat-label">{et.event_type}</span>
                    <span className="stat-value">{et.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Severity Levels Card */}
            <div className="AuditDashboard-card">
              <h3>Severity Levels (Last 30 Days)</h3>
              <div className="AuditDashboard-stats">
                {stats.severities.map((s) => (
                  <div key={s.severity} className="AuditDashboard-stat">
                    <span className="stat-icon">{getSeverityIcon(s.severity)}</span>
                    <span className="stat-label">{s.severity}</span>
                    <span className="stat-value" style={{ color: getSeverityColor(s.severity) }}>
                      {s.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Daily Activity Card */}
            <div className="AuditDashboard-card">
              <h3>Daily Activity (Last 7 Days)</h3>
              <div className="AuditDashboard-chart">
                {stats.dailyActivity.map((day) => (
                  <div key={day.date} className="chart-bar">
                    <div
                      className="chart-bar-fill"
                      style={{
                        height: `${(day.count / Math.max(...stats.dailyActivity.map(d => d.count))) * 100}%`
                      }}
                    />
                    <span className="chart-bar-label">
                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <span className="chart-bar-value">{day.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Users Card */}
            <div className="AuditDashboard-card">
              <h3>Most Active Users</h3>
              <div className="AuditDashboard-list">
                {stats.topUsers.map((u, idx) => (
                  <div key={u.email} className="list-item">
                    <span className="list-rank">#{idx + 1}</span>
                    <span className="list-label">{u.email}</span>
                    <span className="list-value">{u.event_count} events</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Critical Events Table */}
          {stats.criticalEvents.length > 0 && (
            <div className="AuditDashboard-card full-width">
              <h3>=� Recent Critical/Error Events</h3>
              <div className="AuditDashboard-table">
                <table>
                  <thead>
                    <tr>
                      <th>Severity</th>
                      <th>Type</th>
                      <th>Action</th>
                      <th>Details</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.criticalEvents.map((event, idx) => (
                      <tr key={event.id || idx}>
                        <td>
                          <span style={{ color: getSeverityColor(event.severity) }}>
                            {getSeverityIcon(event.severity)} {event.severity}
                          </span>
                        </td>
                        <td>{getEventTypeIcon(event.event_type)} {event.event_type}</td>
                        <td>{event.event_action}</td>
                        <td>
                          <code>{JSON.stringify(event.details || {}).substring(0, 50)}...</code>
                        </td>
                        <td>{new Date(event.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* EVENTS TAB */}
      {activeTab === 'events' && !loading && (
        <div className="AuditDashboard-content">
          {/* Filters */}
          <div className="AuditDashboard-filters">
            <div className="filter-group">
              <label>Event Type:</label>
              <select value={eventTypeFilter} onChange={(e) => setEventTypeFilter(e.target.value)}>
                <option value="">All Types</option>
                <option value="auth">Auth</option>
                <option value="security">Security</option>
                <option value="gdpr">GDPR</option>
                <option value="business">Business</option>
                <option value="system">System</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Severity:</label>
              <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
                <option value="">All Severities</option>
                <option value="critical">Critical</option>
                <option value="error">Error</option>
                <option value="warning">Warning</option>
                <option value="info">Info</option>
              </select>
            </div>

            <button className="filter-clear" onClick={() => { setEventTypeFilter(''); setSeverityFilter(''); }}>
              Clear Filters
            </button>
          </div>

          {/* Events Table */}
          <div className="AuditDashboard-card full-width">
            <h3>=� Audit Events ({events.length})</h3>
            <div className="AuditDashboard-table">
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Severity</th>
                    <th>Type</th>
                    <th>Action</th>
                    <th>User ID</th>
                    <th>IP Address</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id}>
                      <td>{new Date(event.created_at).toLocaleString()}</td>
                      <td>
                        <span style={{ color: getSeverityColor(event.severity) }}>
                          {getSeverityIcon(event.severity)} {event.severity}
                        </span>
                      </td>
                      <td>{getEventTypeIcon(event.event_type)} {event.event_type}</td>
                      <td>{event.event_action}</td>
                      <td>{event.user_id || 'N/A'}</td>
                      <td>{event.ip_address || 'N/A'}</td>
                      <td>
                        <details>
                          <summary>View</summary>
                          <pre>{JSON.stringify(event.details, null, 2)}</pre>
                        </details>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* SECURITY TAB */}
      {activeTab === 'security' && !loading && (
        <div className="AuditDashboard-content">
          <div className="AuditDashboard-card full-width">
            <h3>=� Security Events ({securityEvents.length})</h3>
            <div className="AuditDashboard-table">
              <table>
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Severity</th>
                    <th>Action</th>
                    <th>User ID</th>
                    <th>IP Address</th>
                    <th>Attack Type</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {securityEvents.map((event) => (
                    <tr key={event.id}>
                      <td>{new Date(event.created_at).toLocaleString()}</td>
                      <td>
                        <span style={{ color: getSeverityColor(event.severity) }}>
                          {getSeverityIcon(event.severity)} {event.severity}
                        </span>
                      </td>
                      <td>{event.event_action}</td>
                      <td>{event.user_id || 'N/A'}</td>
                      <td>{event.ip_address || 'N/A'}</td>
                      <td>
                        <code>{event.details?.attack_type || 'N/A'}</code>
                      </td>
                      <td>
                        <details>
                          <summary>View</summary>
                          <pre>{JSON.stringify(event.details, null, 2)}</pre>
                        </details>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Guide Modal */}
      {showGuide && (
        <div className="ModalOverlay" onClick={() => setShowGuide(false)}>
          <div className="AuditDashboard-guide-modal" onClick={(e) => e.stopPropagation()}>
            <div className="guide-header">
              <h2>📖 Audit Dashboard Guide</h2>
              <button className="guide-close" onClick={() => setShowGuide(false)}>×</button>
            </div>
            <div className="guide-content">
              <section>
                <h3>🔍 Event Types</h3>
                <ul>
                  <li><strong>🔐 Auth</strong> - Login success/failure, password changes, account creation</li>
                  <li><strong>🛡️ Security</strong> - File upload rejections, XSS attempts, unauthorized access</li>
                  <li><strong>📋 GDPR</strong> - Data exports, deletion requests, consent management</li>
                  <li><strong>💼 Business</strong> - Purchases, task creation, user engagement</li>
                  <li><strong>⚙️ System</strong> - Errors, backups, performance monitoring</li>
                </ul>
              </section>

              <section>
                <h3>⚠️ Severity Levels</h3>
                <ul>
                  <li><strong>🔵 Info</strong> - Normal operations (login success, task created)</li>
                  <li><strong>🟡 Warning</strong> - Unusual events (failed login, invalid file)</li>
                  <li><strong>🟠 Error</strong> - Problems needing investigation (system errors, repeated failures)</li>
                  <li><strong>🔴 Critical</strong> - Security threats (XSS attempts, data breach)</li>
                </ul>
              </section>

              <section>
                <h3>👤 Track Individual Users</h3>
                <p><strong>Using the Dashboard:</strong></p>
                <ol>
                  <li>Go to "All Events" tab</li>
                  <li>Filter by Event Type (e.g., "auth" for logins)</li>
                  <li>Look at User ID column for specific users</li>
                  <li>Click "View" in Details column for full information</li>
                </ol>
                <p><strong>What You Can See:</strong> Login patterns, IP addresses, actions performed, GDPR requests</p>
              </section>

              <section>
                <h3>💰 Track Purchases</h3>
                <p>Purchase events will appear in the "Business" event type once implemented. You'll be able to:</p>
                <ul>
                  <li>View daily/monthly revenue</li>
                  <li>See top-selling templates</li>
                  <li>Track individual user purchases</li>
                  <li>Calculate conversion rates</li>
                </ul>
              </section>

              <section>
                <h3>📋 GDPR Features</h3>
                <p><strong>Right to Erasure (Account Deletion):</strong></p>
                <ol>
                  <li><strong>Step 1:</strong> User requests deletion → Creates "pending" request</li>
                  <li><strong>Step 2:</strong> 30-day grace period → User can cancel any time</li>
                  <li><strong>Step 3:</strong> Automatic deletion → All data permanently removed</li>
                </ol>
                <p><strong>Right to Access:</strong> Users can export all their data as JSON</p>
                <p><strong>Consent Management:</strong> Track when users give/withdraw consent</p>
              </section>

              <section>
                <h3>🔎 Quick Tips</h3>
                <ul>
                  <li>Use <strong>Overview</strong> tab for high-level statistics</li>
                  <li>Use <strong>All Events</strong> tab to search and filter specific events</li>
                  <li>Use <strong>Security</strong> tab to monitor threats and attacks</li>
                  <li>Click event details to see full JSON data</li>
                  <li>Check "Most Active Users" to identify power users</li>
                  <li>Monitor "Critical Events" for immediate security threats</li>
                </ul>
              </section>

              <section>
                <h3>📊 SQL Query Examples</h3>
                <pre>
{`-- View all activity for user ID 1
SELECT event_type, event_action, created_at
FROM audit_log
WHERE user_id = 1
ORDER BY created_at DESC;

-- Daily revenue (when purchases implemented)
SELECT DATE(created_at) as date,
       COUNT(*) as purchases,
       SUM((details->>'price')::numeric) as revenue
FROM audit_log
WHERE event_action = 'template_purchased'
GROUP BY DATE(created_at);

-- Failed login attempts by IP
SELECT ip_address,
       COUNT(*) as failed_attempts
FROM audit_log
WHERE event_action = 'login_failed'
GROUP BY ip_address
HAVING COUNT(*) > 5;`}
                </pre>
              </section>

              <section>
                <h3>📖 Full Documentation</h3>
                <p>For comprehensive information including:</p>
                <ul>
                  <li>Complete GDPR deletion process walkthrough</li>
                  <li>Advanced analytics queries (DAU/WAU/MAU)</li>
                  <li>Security metrics and attack pattern analysis</li>
                  <li>Compliance metrics and reporting</li>
                  <li>Step-by-step implementation guides</li>
                  <li>Complete event reference table</li>
                </ul>
                <button
                  className="filter-help"
                  onClick={() => { setShowGuide(false); setShowFullDocs(true); }}
                  style={{ marginTop: '12px' }}
                >
                  📄 View Complete Documentation
                </button>
              </section>
            </div>
          </div>
        </div>
      )}

      {/* Full Documentation Modal */}
      {showFullDocs && (
        <div className="ModalOverlay" onClick={() => setShowFullDocs(false)}>
          <div className="AuditDashboard-guide-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1200px' }}>
            <div className="guide-header">
              <h2>📖 Audit Dashboard - Complete Documentation</h2>
              <button className="guide-close" onClick={() => setShowFullDocs(false)}>×</button>
            </div>
            <div className="guide-content">
              {/* Table of Contents */}
              <section>
                <h3>📑 Table of Contents</h3>
                <ol>
                  <li>Understanding Event Types</li>
                  <li>Understanding Severity Levels</li>
                  <li>Tracking Individual User Activity</li>
                  <li>Tracking Purchases Over Time</li>
                  <li>GDPR Features Explained</li>
                  <li>Data Deletion Request Process</li>
                  <li>Advanced Analytics Queries</li>
                </ol>
              </section>

              {/* Event Types - Detailed */}
              <section>
                <h3>🔍 Understanding Event Types</h3>

                <h4>1. 🔐 Auth Events</h4>
                <p><strong>Purpose:</strong> Track user authentication and account access</p>
                <p><strong>Events Logged:</strong></p>
                <ul>
                  <li><code>login_success</code> - User successfully logged in</li>
                  <li><code>login_failed</code> - Failed login attempt</li>
                  <li><code>logout</code> - User logged out (when implemented)</li>
                  <li><code>password_changed</code> - Password was changed (when implemented)</li>
                  <li><code>account_created</code> - New account registered (when implemented)</li>
                </ul>
                <p><strong>What You Can Learn:</strong></p>
                <ul>
                  <li>Who's logging into the system and when</li>
                  <li>Failed login attempts (potential security threats)</li>
                  <li>Account access patterns</li>
                  <li>User session activity</li>
                </ul>

                <h4>2. 🛡️ Security Events</h4>
                <p><strong>Purpose:</strong> Track security threats and rejected malicious actions</p>
                <p><strong>Events Logged:</strong></p>
                <ul>
                  <li><code>file_upload_rejected</code> - User tried to upload invalid/malicious file</li>
                  <li><code>base64_image_rejected</code> - Invalid image in card background/wallpaper</li>
                  <li><code>xss_attempt</code> - SVG file with potential XSS payload blocked</li>
                  <li><code>rate_limit_exceeded</code> - Too many requests (when implemented)</li>
                </ul>

                <h4>3. 📋 GDPR Events</h4>
                <p><strong>Purpose:</strong> Track data privacy and compliance actions</p>
                <p><strong>Events Logged:</strong></p>
                <ul>
                  <li><code>right_to_erasure_requested</code> - User requested account deletion</li>
                  <li><code>deletion_request_cancelled</code> - User cancelled deletion request</li>
                  <li><code>data_export_requested</code> - User downloaded their data</li>
                  <li><code>consent_given</code> - User gave consent for data processing</li>
                  <li><code>consent_withdrawn</code> - User withdrew consent</li>
                </ul>

                <h4>4. 💼 Business Events (To Be Implemented)</h4>
                <p><strong>Purpose:</strong> Track revenue-generating actions and user engagement</p>
                <p><strong>Events To Be Logged:</strong></p>
                <ul>
                  <li><code>template_purchased</code> - User bought a template from marketplace</li>
                  <li><code>subscription_upgraded</code> - User upgraded to paid tier</li>
                  <li><code>task_created</code> - User created a new task</li>
                  <li><code>workspace_created</code> - User created new workspace</li>
                </ul>

                <h4>5. ⚙️ System Events (To Be Implemented)</h4>
                <p><strong>Purpose:</strong> Track system health and operational events</p>
                <p><strong>Events To Be Logged:</strong></p>
                <ul>
                  <li><code>backup_completed</code> - Database backup finished</li>
                  <li><code>migration_applied</code> - Database schema update</li>
                  <li><code>error_occurred</code> - System error logged</li>
                </ul>
              </section>

              {/* Severity Levels */}
              <section>
                <h3>⚠️ Understanding Severity Levels</h3>

                <h4>🔵 Info (Normal Operations)</h4>
                <p><strong>When Used:</strong> Regular, expected operations</p>
                <ul>
                  <li>User logged in successfully</li>
                  <li>User exported their data</li>
                  <li>Task created</li>
                  <li>Consent given</li>
                </ul>
                <p><strong>Response:</strong> No action needed - just tracking normal activity</p>

                <h4>🟡 Warning (Attention Needed)</h4>
                <p><strong>When Used:</strong> Unusual but not dangerous events</p>
                <ul>
                  <li>Invalid file upload attempt (non-malicious)</li>
                  <li>Login failure (could be typo)</li>
                  <li>Mild rate limiting</li>
                </ul>
                <p><strong>Response:</strong> Monitor for patterns - may indicate user confusion or minor issues</p>

                <h4>🟠 Error (Problem Detected)</h4>
                <p><strong>When Used:</strong> Something went wrong that needs investigation</p>
                <ul>
                  <li>Repeated login failures</li>
                  <li>System errors</li>
                  <li>Failed backups</li>
                  <li>Data integrity issues</li>
                </ul>
                <p><strong>Response:</strong> Investigate and fix the root cause</p>

                <h4>🔴 Critical (Immediate Action Required)</h4>
                <p><strong>When Used:</strong> Security threats or major system failures</p>
                <ul>
                  <li>SVG XSS attempt detected</li>
                  <li>SQL injection attempt</li>
                  <li>System compromise indicators</li>
                  <li>Data breach attempts</li>
                </ul>
                <p><strong>Response:</strong> Immediate investigation and potential account suspension</p>
              </section>

              {/* Tracking Users */}
              <section>
                <h3>👤 Tracking Individual User Activity</h3>

                <h4>Method 1: Using the Dashboard (UI)</h4>
                <ol>
                  <li>Go to <strong>Admin → Audit Logs → All Events tab</strong></li>
                  <li>Filter by Event Type: Select "auth" to see login patterns</li>
                  <li>Look at User ID column: Find events for specific user</li>
                  <li>Click "View" in Details column: See full event information</li>
                </ol>

                <h4>Method 2: Using Database Queries</h4>
                <pre>
{`-- View all activity for a specific user
SELECT event_type, event_action, severity, created_at, ip_address, details
FROM audit_log
WHERE user_id = 1
ORDER BY created_at DESC LIMIT 100;

-- Track user login frequency
SELECT DATE(created_at) as date, COUNT(*) as login_count
FROM audit_log
WHERE user_id = 1 AND event_action = 'login_success'
GROUP BY DATE(created_at)
ORDER BY date DESC;`}
                </pre>

                <h4>Method 3: Using the API</h4>
                <pre>
{`# Get all events for user ID 1
curl process.env.REACT_APP_API_URL || "http://localhost:3001/api/v1/audit/events?limit=100" \\
  -H "Authorization: Bearer $TOKEN" \\
  | jq '.events[] | select(.user_id == 1)'`}
                </pre>
              </section>

              {/* GDPR Features */}
              <section>
                <h3>📋 GDPR Features Explained</h3>

                <p><strong>What is GDPR?</strong></p>
                <p>GDPR (General Data Protection Regulation) is EU law that protects user data privacy. It gives users rights over their personal data:</p>
                <ol>
                  <li><strong>Right to Access</strong> - Users can request all data you hold about them</li>
                  <li><strong>Right to Erasure</strong> - Users can request account deletion</li>
                  <li><strong>Right to Data Portability</strong> - Users can export their data</li>
                  <li><strong>Right to Consent</strong> - Users must explicitly consent to data processing</li>
                </ol>

                <h4>How Our System Implements GDPR</h4>

                <p><strong>1. Right to Access</strong></p>
                <ul>
                  <li>User calls <code>GET /api/v1/gdpr/export</code></li>
                  <li>Returns JSON with all user data: profile, workspaces, tasks, comments, consents</li>
                  <li>Audit event logged: <code>data_export_requested</code></li>
                </ul>

                <p><strong>2. Right to Erasure (Account Deletion)</strong></p>
                <ul>
                  <li>User clicks "Delete My Account" button</li>
                  <li>System creates deletion request with 30-day grace period</li>
                  <li>User can cancel during the 30 days</li>
                  <li>After 30 days, background job permanently deletes all data</li>
                </ul>

                <p><strong>3. Consent Management</strong></p>
                <ul>
                  <li>Track when users give/withdraw consent</li>
                  <li>Store: consent type, given/withdrawn, version, timestamp, IP, user agent</li>
                  <li>Used to prove compliance in audits</li>
                </ul>
              </section>

              {/* Data Deletion Process */}
              <section>
                <h3>🗑️ Data Deletion Request Process</h3>

                <h4>Step 1: User Requests Deletion</h4>
                <p>User clicks "Delete My Account" button in settings</p>
                <ul>
                  <li>Request created with status "pending"</li>
                  <li>Deletion scheduled for 30 days from now</li>
                  <li>Audit log entry created: <code>right_to_erasure_requested</code></li>
                  <li>User receives confirmation with cancellation instructions</li>
                </ul>

                <h4>Step 2: Grace Period (30 Days)</h4>
                <ul>
                  <li>User can still login and use the app normally</li>
                  <li>Banner shows: "Your account is scheduled for deletion on [date]. [Cancel Request]"</li>
                  <li>User can cancel any time by calling <code>POST /api/v1/gdpr/deletion-request/cancel</code></li>
                </ul>

                <h4>Step 3: Deletion Execution (After 30 Days)</h4>
                <p>Background cron job runs daily checking for due deletions</p>
                <p><strong>What Gets Deleted:</strong></p>
                <ul>
                  <li>✅ User profile</li>
                  <li>✅ All workspaces owned by user</li>
                  <li>✅ All lists in those workspaces</li>
                  <li>✅ All tasks in those lists</li>
                  <li>✅ All comments by user</li>
                  <li>✅ All permissions/invites</li>
                  <li>✅ All consent records</li>
                  <li>✅ User's personal data in audit logs (anonymized)</li>
                </ul>
                <p><strong>What Gets Kept (Anonymized):</strong></p>
                <ul>
                  <li>📊 Audit log entries (user_id set to NULL) - for compliance/security records</li>
                  <li>📊 Aggregated analytics (no personal identifiers)</li>
                </ul>
              </section>

              {/* Advanced Analytics */}
              <section>
                <h3>📊 Advanced Analytics Queries</h3>

                <h4>User Engagement Metrics</h4>
                <pre>
{`-- Daily Active Users (DAU)
SELECT DATE(created_at) as date, COUNT(DISTINCT user_id) as daily_active_users
FROM audit_log
WHERE event_type IN ('auth', 'business')
  AND created_at >= NOW() - INTERVAL '30 days'
GROUP BY DATE(created_at)
ORDER BY date DESC;

-- Weekly Active Users (WAU)
SELECT DATE_TRUNC('week', created_at) as week,
       COUNT(DISTINCT user_id) as weekly_active_users
FROM audit_log
WHERE event_type IN ('auth', 'business')
  AND created_at >= NOW() - INTERVAL '90 days'
GROUP BY DATE_TRUNC('week', created_at)
ORDER BY week DESC;

-- Monthly Active Users (MAU)
SELECT DATE_TRUNC('month', created_at) as month,
       COUNT(DISTINCT user_id) as monthly_active_users
FROM audit_log
WHERE event_type IN ('auth', 'business')
GROUP BY DATE_TRUNC('month', created_at)
ORDER BY month DESC;`}
                </pre>

                <h4>Security Metrics</h4>
                <pre>
{`-- Failed login attempts by IP
SELECT ip_address, COUNT(*) as failed_attempts, MAX(created_at) as last_attempt
FROM audit_log
WHERE event_action = 'login_failed'
  AND created_at >= NOW() - INTERVAL '7 days'
GROUP BY ip_address
HAVING COUNT(*) > 5
ORDER BY failed_attempts DESC;

-- Attack patterns
SELECT details->>'attack_type' as attack_type,
       COUNT(*) as incidents,
       COUNT(DISTINCT ip_address) as unique_ips,
       MAX(created_at) as last_incident
FROM audit_log
WHERE event_type = 'security'
  AND created_at >= NOW() - INTERVAL '30 days'
GROUP BY attack_type
ORDER BY incidents DESC;`}
                </pre>

                <h4>Compliance Metrics</h4>
                <pre>
{`-- GDPR requests per month
SELECT TO_CHAR(created_at, 'YYYY-MM') as month,
       event_action,
       COUNT(*) as requests
FROM audit_log
WHERE event_type = 'gdpr'
GROUP BY TO_CHAR(created_at, 'YYYY-MM'), event_action
ORDER BY month DESC, event_action;`}
                </pre>
              </section>

              {/* Quick Reference */}
              <section>
                <h3>📑 Quick Reference: Event Actions</h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '12px' }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-secondary)' }}>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid var(--border-light)' }}>Event Type</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid var(--border-light)' }}>Event Action</th>
                      <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid var(--border-light)' }}>When It Fires</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>auth</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>login_success</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>User logged in successfully</td></tr>
                    <tr><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>auth</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>login_failed</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>Login attempt failed</td></tr>
                    <tr><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>security</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>file_upload_rejected</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>Invalid file upload blocked</td></tr>
                    <tr><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>security</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>base64_image_rejected</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>Invalid image blocked</td></tr>
                    <tr><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>gdpr</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>right_to_erasure_requested</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>Deletion request submitted</td></tr>
                    <tr><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>gdpr</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>deletion_request_cancelled</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>Deletion request cancelled</td></tr>
                    <tr><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>gdpr</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>data_export_requested</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>User exported their data</td></tr>
                    <tr><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>business</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>template_purchased</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>Template bought (to implement)</td></tr>
                    <tr><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>business</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>task_created</td><td style={{ padding: '8px', borderBottom: '1px solid var(--border-light)' }}>Task created (to implement)</td></tr>
                  </tbody>
                </table>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
